import mixpanel from 'mixpanel-browser';

class MixpanelAnalytics {
    private initialized = false;

    constructor() {
        this.init();
    }

    private init() {
        // Only initialize in production
        if (!this.isProduction()) {
            return;
        }

        const token = this.getToken();
        if (!token) {
            return;
        }

        mixpanel.init(token, {
            debug: false,
            track_pageview: false,
            persistence: 'localStorage',
        });
        this.initialized = true;
    }

    private isProduction(): boolean {
        return import.meta.env.REACT_APP_ENV === 'prod';
    }

    private getToken(): string {
        if (this.isProduction()) {
            return import.meta.env.REACT_APP_MIXPANEL_TOKEN || '';
        }
        return '';
    }

    /**
     * Track an event with properties
     */
    track(eventName: string, properties?: Record<string, any>) {
        if (import.meta.env.NODE_ENV === 'development') {
            console.log('Mixpanel event:', eventName, properties);
        }

        if (!this.isProduction()) {
            return; // Silent skip in non-production
        }

        if (!this.initialized) {
            console.warn('Mixpanel not initialized, skipping event:', eventName);
            return;
        }

        try {
            mixpanel.track(eventName, properties);
        } catch (error) {
            console.error('Failed to track Mixpanel event:', error);
        }
    }

    /**
     * Identify a user
     */
    identify(userId: string, properties?: Record<string, any>) {
        if (!this.isProduction() || !this.initialized) return;

        try {
            mixpanel.identify(userId);
            if (properties) {
                mixpanel.people.set(properties);
                mixpanel.register(properties);
            }
        } catch (error) {
            console.error('Failed to identify user in Mixpanel:', error);
        }
    }

    /**
     * Associate a user with a group
     * @param groupKey - The group key (e.g., 'account_id', 'company_id')
     * @param groupId - The group identifier (e.g., specific account ID)
     */
    setGroup(groupKey: string, groupId: string | string[]) {
        if (!this.isProduction() || !this.initialized) return;

        try {
            mixpanel.set_group(groupKey, groupId);

            // Mixpanel requires group keys on user profiles to be arrays for linking
            const groupIdArray = Array.isArray(groupId) ? groupId : [groupId];
            const profileUpdate: Record<string, any> = {};
            profileUpdate[groupKey] = groupIdArray;
            mixpanel.people.set(profileUpdate);
        } catch (error) {
            console.error('Failed to set Mixpanel group:', error);
        }
    }

    /**
     * Set properties on a group profile
     * @param groupKey - The group key (e.g., 'account_id', 'company_id')
     * @param groupId - The group identifier
     * @param properties - Properties to set on the group profile
     */
    setGroupProfile(groupKey: string, groupId: string, properties: Record<string, any>) {
        if (!this.isProduction() || !this.initialized) return;

        try {
            mixpanel.get_group(groupKey, groupId).set(properties);
        } catch (error) {
            console.error('Failed to set Mixpanel group profile:', error);
        }
    }

    /**
     * Set properties on a group profile only once
     * @param groupKey - The group key (e.g., 'account_id', 'company_id')
     * @param groupId - The group identifier
     * @param properties - Properties to set once on the group profile
     */
    setGroupProfileOnce(groupKey: string, groupId: string, properties: Record<string, any>) {
        if (!this.isProduction() || !this.initialized) return;

        try {
            mixpanel.get_group(groupKey, groupId).set_once(properties);
        } catch (error) {
            console.error('Failed to set Mixpanel group profile once:', error);
        }
    }

    /**
     * Reset user session
     */
    reset() {
        if (!this.isProduction() || !this.initialized) return;

        try {
            mixpanel.reset();
        } catch (error) {
            console.error('Failed to reset Mixpanel:', error);
        }
    }
}

// Export singleton instance
export const analytics = new MixpanelAnalytics();
export default analytics;
